import { useState } from "react";
import {
  AWS_DOCUMENT_CHECKLIST,
  AWS_FORM_STEPS,
  AWS_FUNDING_STATUS_OPTIONS,
  AWS_INDUSTRY_OPTIONS,
  AWS_MONTHLY_SPEND_OPTIONS,
  AWS_STARTUP_STAGE_OPTIONS,
  AWS_TEAM_SIZE_OPTIONS,
} from "../data/awsActivateContent";
import { submitAwsActivateApplication } from "../api/cmsApi";

const INITIAL_FORM = {
  companyName: "",
  founderName: "",
  registeredName: "",
  country: "India",
  state: "",
  city: "",
  website: "",
  linkedin: "",
  industry: "",
  teamSize: "",
  yearEstablished: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  jobTitle: "",
  contactMethod: "email",
  message: "",
  awsAccountEmail: "",
  awsAccountId: "",
  awsRootEmail: "",
  awsOrgId: "",
  monthlySpend: "",
  startupStage: "",
  fundingStatus: "",
  investorName: "",
  documents: [],
  agreeTerms: false,
};

export default function AwsActivateForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const next = () => setStep((s) => Math.min(s + 1, AWS_FORM_STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const documentNames = (form.documents || []).map((f) => f?.name).filter(Boolean);
      const { documents, agreeTerms, ...rest } = form;
      await submitAwsActivateApplication({
        ...rest,
        documentNames,
        agreeTerms: !!agreeTerms,
      });
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setStep(0);
        setForm(INITIAL_FORM);
      }, 5000);
    } catch (err) {
      setError(err?.message || "Could not submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="aws-form-success">
        <i className="bi bi-check-circle-fill" aria-hidden="true" />
        <h3>Application Submitted!</h3>
        <p>Thank you for applying. Our AWS Activate team will review your application and contact you within 2 business days.</p>
      </div>
    );
  }

  return (
    <div className="aws-form-layout">
      <aside className="aws-form-steps" aria-label="Application steps">
        {AWS_FORM_STEPS.map((label, idx) => (
          <button
            key={label}
            type="button"
            className={`aws-form-step${idx === step ? " is-active" : ""}${idx < step ? " is-done" : ""}`}
            onClick={() => idx < step && setStep(idx)}
          >
            <span className="aws-form-step-num">{idx + 1}</span>
            <span className="aws-form-step-label">{label}</span>
          </button>
        ))}
      </aside>

      <form className="aws-form-card" onSubmit={handleSubmit}>
        <div className="aws-form-card-head">
          <h3>{AWS_FORM_STEPS[step]}</h3>
          <span>Step {step + 1} of {AWS_FORM_STEPS.length}</span>
        </div>

        <div className="aws-form-fields">
          {step === 0 && (
            <>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="aws-form-label">Company Name</label>
                  <input className="aws-form-input" value={form.companyName} onChange={(e) => update("companyName", e.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">Founder Name</label>
                  <input className="aws-form-input" value={form.founderName} onChange={(e) => update("founderName", e.target.value)} required />
                </div>
                <div className="col-12">
                  <label className="aws-form-label">Registered Business Name</label>
                  <input className="aws-form-input" value={form.registeredName} onChange={(e) => update("registeredName", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="aws-form-label">Country</label>
                  <input className="aws-form-input" value={form.country} onChange={(e) => update("country", e.target.value)} required />
                </div>
                <div className="col-md-4">
                  <label className="aws-form-label">State</label>
                  <input className="aws-form-input" value={form.state} onChange={(e) => update("state", e.target.value)} required />
                </div>
                <div className="col-md-4">
                  <label className="aws-form-label">City</label>
                  <input className="aws-form-input" value={form.city} onChange={(e) => update("city", e.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">Company Website</label>
                  <input type="url" className="aws-form-input" placeholder="https://" value={form.website} onChange={(e) => update("website", e.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">LinkedIn Profile</label>
                  <input type="url" className="aws-form-input" placeholder="https://linkedin.com/company/..." value={form.linkedin} onChange={(e) => update("linkedin", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="aws-form-label">Industry</label>
                  <select className="aws-form-input" value={form.industry} onChange={(e) => update("industry", e.target.value)} required>
                    <option value="">Select industry</option>
                    {AWS_INDUSTRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="aws-form-label">Team Size</label>
                  <select className="aws-form-input" value={form.teamSize} onChange={(e) => update("teamSize", e.target.value)} required>
                    <option value="">Select size</option>
                    {AWS_TEAM_SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="aws-form-label">Year Established</label>
                  <input type="number" className="aws-form-input" min="2000" max="2026" value={form.yearEstablished} onChange={(e) => update("yearEstablished", e.target.value)} required />
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="aws-form-label">Full Name</label>
                  <input className="aws-form-input" value={form.contactName} onChange={(e) => update("contactName", e.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">Email Address</label>
                  <input type="email" className="aws-form-input" value={form.contactEmail} onChange={(e) => update("contactEmail", e.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">Phone Number</label>
                  <input className="aws-form-input" value={form.contactPhone} onChange={(e) => update("contactPhone", e.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">Job Title</label>
                  <input className="aws-form-input" value={form.jobTitle} onChange={(e) => update("jobTitle", e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="aws-form-label">Preferred Contact Method</label>
                  <div className="aws-form-radios">
                    {["email", "phone", "whatsapp"].map((m) => (
                      <label key={m} className="aws-form-radio">
                        <input type="radio" name="contactMethod" checked={form.contactMethod === m} onChange={() => update("contactMethod", m)} />
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-12">
                  <label className="aws-form-label">Message (Optional)</label>
                  <textarea className="aws-form-input aws-form-textarea" rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} />
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="aws-form-label">AWS Account Email</label>
                  <input type="email" className="aws-form-input" value={form.awsAccountEmail} onChange={(e) => update("awsAccountEmail", e.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">AWS Account ID</label>
                  <input className="aws-form-input" placeholder="12-digit account ID" value={form.awsAccountId} onChange={(e) => update("awsAccountId", e.target.value)} required />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">AWS Root Email</label>
                  <input type="email" className="aws-form-input" value={form.awsRootEmail} onChange={(e) => update("awsRootEmail", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">Organization ID (Optional)</label>
                  <input className="aws-form-input" value={form.awsOrgId} onChange={(e) => update("awsOrgId", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">Current Monthly AWS Spend</label>
                  <select className="aws-form-input" value={form.monthlySpend} onChange={(e) => update("monthlySpend", e.target.value)} required>
                    <option value="">Select range</option>
                    {AWS_MONTHLY_SPEND_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="aws-form-label">Startup Stage</label>
                  <select className="aws-form-input" value={form.startupStage} onChange={(e) => update("startupStage", e.target.value)} required>
                    <option value="">Select stage</option>
                    {AWS_STARTUP_STAGE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="aws-form-label">Funding Status</label>
                  <select className="aws-form-input" value={form.fundingStatus} onChange={(e) => update("fundingStatus", e.target.value)} required>
                    <option value="">Select status</option>
                    {AWS_FUNDING_STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="aws-form-label">Investor Name (If applicable)</label>
                  <input className="aws-form-input" value={form.investorName} onChange={(e) => update("investorName", e.target.value)} />
                </div>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="aws-form-upload">
                <i className="bi bi-cloud-upload" aria-hidden="true" />
                <p>Drag &amp; drop files here or click to browse</p>
                <input type="file" multiple className="aws-form-file" onChange={(e) => update("documents", Array.from(e.target.files || []))} />
              </div>
              <ul className="aws-form-checklist">
                {AWS_DOCUMENT_CHECKLIST.map((doc) => (
                  <li key={doc}>
                    <i className="bi bi-check-circle-fill" aria-hidden="true" />
                    {doc}
                  </li>
                ))}
              </ul>
            </>
          )}

          {step === 5 && (
            <div className="aws-form-review">
              <div className="aws-form-review-block">
                <h4>Company</h4>
                <p>{form.companyName || "—"} · {form.city}, {form.state}</p>
                <p>{form.website || "—"}</p>
              </div>
              <div className="aws-form-review-block">
                <h4>Contact</h4>
                <p>{form.contactName || "—"} · {form.contactEmail || "—"}</p>
                <p>{form.contactPhone || "—"}</p>
              </div>
              <div className="aws-form-review-block">
                <h4>AWS Account</h4>
                <p>ID: {form.awsAccountId || "—"}</p>
                <p>Spend: {form.monthlySpend || "—"}</p>
              </div>
              <label className="aws-form-checkbox">
                <input type="checkbox" checked={form.agreeTerms} onChange={(e) => update("agreeTerms", e.target.checked)} required />
                I confirm that all information provided is accurate and I agree to Emicron&apos;s terms for AWS Activate application support.
              </label>
            </div>
          )}
        </div>

        <div className="aws-form-actions">
          {error ? <div className="text-danger small me-auto">{error}</div> : null}
          {step > 0 && (
            <button type="button" className="aws-btn aws-btn-outline" onClick={back} disabled={submitting}>
              Back
            </button>
          )}
          {step < AWS_FORM_STEPS.length - 1 ? (
            <button type="button" className="aws-btn aws-btn-primary" onClick={next}>
              Continue
            </button>
          ) : (
            <button type="submit" className="aws-btn aws-btn-primary" disabled={!form.agreeTerms || submitting}>
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          )}
        </div>
      </form>

      <aside className="aws-form-sidebar">
        <div className="aws-form-sidebar-card">
          <i className="bi bi-clipboard-check" aria-hidden="true" />
          <h4>Why this matters?</h4>
          <p>Complete and accurate information helps us fast-track your AWS Activate application and maximize your credit eligibility.</p>
        </div>
      </aside>
    </div>
  );
}
